import { X } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Button } from '../../../components/primitives/Button';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Caption, Heading4 } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { useUpdateUserPreferences, useUserPreferences } from '../../../lib/useUserPreferences';
import { colors } from '../../../theme/colors';
import { dismissTour, isTourDismissed } from '../lib/onboarding-prefs';

const TOUR_STEP_KEYS = ['welcome', 'preview', 'analytics'] as const;

interface OnboardingTourProps {
  onDismiss?: () => void;
}

export function OnboardingTour({ onDismiss }: OnboardingTourProps) {
  const { t } = useTranslation();
  const mp = useMixpanel();
  // Backend pref is authoritative across devices; localStorage is the
  // synchronous fallback so we don't briefly render the tour while the
  // query is in flight on web. On native localStorage is a no-op so the
  // tour shows until the backend pref returns — acceptable for v1.
  const { data: prefs } = useUserPreferences();
  const updatePrefs = useUpdateUserPreferences();
  const [visible, setVisible] = useState(() => !isTourDismissed());
  const [stepIndex, setStepIndex] = useState(0);

  // Hide once the backend confirms the user dismissed earlier on another device.
  if (prefs?.skipVoiceOnboardingTour === true && visible) {
    setVisible(false);
  }

  const handleClose = useCallback(
    (completedAll: boolean) => {
      dismissTour(); // sync local fallback
      updatePrefs.mutate({ skipVoiceOnboardingTour: true }); // cross-device persistence
      trackEvent(mp, 'voice_agent.tour_dismissed', { completed_all_tips: completedAll });
      setVisible(false);
      onDismiss?.();
    },
    [mp, onDismiss, updatePrefs],
  );

  if (!visible) return null;
  const stepKey = TOUR_STEP_KEYS[stepIndex];
  const isLast = stepIndex === TOUR_STEP_KEYS.length - 1;

  return (
    <View
      style={styles.overlay}
      accessibilityViewIsModal
      accessible
      accessibilityLabel={t('voiceAgents.tour.a11yLabel')}
      testID="voice-agents-onboarding-tour"
    >
      <View style={styles.card}>
        <HStack style={styles.header}>
          <Caption style={styles.counter}>
            {t('voiceAgents.tour.tip')} {stepIndex + 1} {t('voiceAgents.tour.of')}{' '}
            {TOUR_STEP_KEYS.length}
          </Caption>
          <Pressable
            onPress={() => handleClose(false)}
            accessibilityLabel={t('voiceAgents.tour.dismissA11y')}
            accessibilityRole="button"
            hitSlop={8}
          >
            <X size={18} color={colors.text.secondary} />
          </Pressable>
        </HStack>

        <VStack spacing="xs" style={styles.body}>
          <Heading4 accessibilityRole="header">
            {t(`voiceAgents.tour.steps.${stepKey}.title`)}
          </Heading4>
          <Body style={styles.bodyText}>{t(`voiceAgents.tour.steps.${stepKey}.body`)}</Body>
        </VStack>

        <HStack spacing="md" style={styles.footer}>
          <Button
            variant="ghost"
            onPress={() => handleClose(false)}
            accessibilityLabel={t('voiceAgents.tour.skipA11y')}
            style={styles.footerBtn}
          >
            {t('voiceAgents.tour.skip')}
          </Button>
          <Button
            variant="primary"
            onPress={() => {
              if (isLast) handleClose(true);
              else setStepIndex((i) => i + 1);
            }}
            accessibilityLabel={
              isLast ? t('voiceAgents.tour.finishA11y') : t('voiceAgents.tour.nextA11y')
            }
            style={styles.footerBtn}
          >
            {isLast ? t('voiceAgents.tour.gotIt') : t('voiceAgents.tour.next')}
          </Button>
        </HStack>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    zIndex: 100,
  },
  card: {
    backgroundColor: colors.background.secondary,
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 420,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  counter: {
    color: colors.text.tertiary,
  },
  body: {
    marginBottom: 20,
  },
  bodyText: {
    color: colors.text.secondary,
    lineHeight: 20,
  },
  footer: {
    justifyContent: 'flex-end',
  },
  footerBtn: {
    minWidth: 100,
  },
});
